import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { LogOut, ClipboardList, AlertTriangle, ArrowLeftRight } from 'lucide-react';

import { Logo } from '@/components/brand/Logo';
import { useAuthStore } from '@/lib/auth';
import { cn } from '@/lib/cn';

/**
 * /supervisor layout -- a facility desk/tablet surface, not a mobile one
 * (unlike DriverShell): a top bar with the two destinations (Queue,
 * Inspections) rather than a sidebar, since there's nothing else here.
 */
export function SupervisorShell() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);

  function signOut() {
    clear();
    navigate('/login');
  }

  return (
    <div className="min-h-screen bg-bone">
      <header className="bg-ink text-bone">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <Logo variant="light" compact asLink={false} />
            <nav className="flex items-center gap-1">
              <NavLink to="/supervisor" end
                       className={({ isActive }) => cn(
                         'flex items-center gap-1.5 px-3 py-1.5 rounded-xs font-body text-sm transition-colors',
                         isActive ? 'bg-gold/15 text-gold' : 'text-bone/70 hover:text-bone hover:bg-bone/5',
                       )}>
                <ClipboardList size={15} strokeWidth={1.5} /> Queue
              </NavLink>
              <NavLink to="/supervisor/inspections"
                       className={({ isActive }) => cn(
                         'flex items-center gap-1.5 px-3 py-1.5 rounded-xs font-body text-sm transition-colors',
                         isActive ? 'bg-gold/15 text-gold' : 'text-bone/70 hover:text-bone hover:bg-bone/5',
                       )}>
                <AlertTriangle size={15} strokeWidth={1.5} /> Inspections
              </NavLink>
              <NavLink to="/supervisor/handovers"
                       className={({ isActive }) => cn(
                         'flex items-center gap-1.5 px-3 py-1.5 rounded-xs font-body text-sm transition-colors',
                         isActive ? 'bg-gold/15 text-gold' : 'text-bone/70 hover:text-bone hover:bg-bone/5',
                       )}>
                <ArrowLeftRight size={15} strokeWidth={1.5} /> Handovers
              </NavLink>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <span className="font-mono text-2xs uppercase tracking-wider text-bone/50 hidden sm:inline">
              {user?.name || 'Supervisor'}
            </span>
            <button type="button" onClick={signOut}
                    className="inline-flex items-center gap-1.5 font-body text-xs text-bone/60 hover:text-bone transition-colors">
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-8">
        <Outlet />
      </main>
    </div>
  );
}
